import React from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { getUserCredential } from "src/lib/cookies";
import {
  GetAllSelectOperators,
  getApprovalStatus,
  GetEventCompPlanFilterAPI,
  GetEventDataSourceFilterAPI,
  GetEventPositionFilterAPI,
  GetEventServiceLocationFilterAPI,
  GetEventTypeAPI,
  getProcessStatus,
  GetUnitType,
} from "src/lib/common-service";
import { unionBy } from "lodash";
import { sortOperators } from "./filterUtils";

export const useQueryFilterData = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const cookiesData = getUserCredential();

  const [operatorList, setOperatorList] = React.useState({});
  const [eventTypeFields, setEventTypeFieldsData] = React.useState({
    eventTypeValue: {},
    eventTypeData: [],
  });
  const [eventTypeFieldList, setEventTypeFieldList] = React.useState([]);

  const [eventDataSource, setEventDataSource] = React.useState({
    eventDataSourceValue: {},
    eventDataSourceData: [],
    isEventDataSourceDataLoading: false,
    originalData: [],
  });

  const [serviceLocationSource, setServiceLocationSource] = React.useState({
    serviceLocationValue: {},
    serviceLocationData: [],
    isServiceLocationLoading: false,
    originalData: [],
  });

  const [compPlanSource, setCompPlanSource] = React.useState({
    compPlanValue: {},
    compPlanData: [],
    isCompPlanLoading: false,
    originalData: [],
  });

  const [positionSourceData, setPositionSourceData] = React.useState({
    positionValue: {},
    positionData: [],
    isPositionDataLoading: false,
    originalData: [],
  });

  const [approvalStatusListData, setApprovalStatusListData] = React.useState({
    approvalStatusValue: {},
    approvalStatusData: [],
    isApprovalStatusLoading: false,
    originalData: [],
  });

  const [unitTypeDataList, setUnitTypeDataList] = React.useState({
    unitTypeValue: {},
    unitTypeData: [],
    isUnitTypeLoading: false,
    originalData: [],
  });

  const [dateFields, setDateFields] = React.useState({
    in_period_start_date: null,
    in_period_end_date: null,
  });

  const [isEventTypeChange, setIsEventTypeChange] = React.useState(false);

  const [totalAmtUnitTypeDataList, setTotalAmtUnitTypeDataList] = React.useState({
    totalAmtUnitTypeValue: {},
    totalAmtTypeData: [],
    isTotalAmtUnitTypeLoading: false,
    originalData: [],
  });

  const [processStatusListData, setProcessStatusListData] = React.useState({
    processStatusValue: {},
    processStatusData: [],
    isProcessStatusLoading: false,
    originalData: [],
  });

  // Initialize operators
  const fetchOperators = async () => {
    let response = await GetAllSelectOperators(null, dispatch);
    if (response) {
      let res = sortOperators(response.stringOperator);
      response.stringOperator = res;
      setOperatorList(response);
    }
  };

  const getEventTypeDropdown = () => {
    const eventTypeData = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_filter: null,
      in_sort_number: 3,
      in_sort_order: "ASC",
      in_page_row_offset: 0,
      in_page_rows: 9999,
    };

    return GetEventTypeAPI(eventTypeData, dispatch, history)
      .then((res) => {
        setEventTypeFieldsData((prev) => ({
          ...prev,
          eventTypeValue: res?.[0] || null,
          eventTypeData: res || [],
        }));

        setIsEventTypeChange(true);
        setEventTypeFieldList(res || []);
      })
      .catch((err) => {
        console.error("Failed to load event types", err);
      });
  };

  const getEventDataSourceDataListAsync = async (eventData) => {
    setEventDataSource((prev) => ({
      ...prev,
      isEventDataSourceDataLoading: true,
      eventDataSourceData: [],
      eventDataSourceValue: {},
      originalData: [],
    }));

    const eventDataSourceObj = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_filter: null,
      in_event_type_mstr_key: eventData.eventTypeValue ? eventData.eventTypeValue.event_type_mstr_key : eventData,
      in_sort_number: 1,
      in_sort_order: "ASC",
    };

    const resName = await GetEventDataSourceFilterAPI(eventDataSourceObj, dispatch, history);
    if (resName.length > 0) {
      const tempArray = resName.map((item) => ({
        text: item.event_data_source,
        id: item.event_data_source,
        dataItemKey: "event_data_source",
      }));
      setEventDataSource({
        eventDataSourceData: tempArray,
        originalData: tempArray,
        isEventDataSourceDataLoading: false,
      });
    } else {
      setEventDataSource({
        isEventDataSourceDataLoading: false,
        eventDataSourceData: [],
        originalData: [],
      });
    }
    return resName;
  };

  const getServiceLocation = async (eventData) => {
    setServiceLocationSource((prev) => ({
      ...prev,
      isServiceLocationLoading: true,
      serviceLocationData: [],
      serviceLocationValue: {},
      originalData: [],
    }));

    const sourceObj = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_filter: null,
      in_event_type_mstr_key: eventData.eventTypeValue ? eventData.eventTypeValue.event_type_mstr_key : eventData,
      in_sort_number: 1,
      in_sort_order: "ASC",
    };

    const resName = await GetEventServiceLocationFilterAPI(sourceObj, dispatch, history);

    if (resName.length > 0) {
      const tempArray = resName.map((item) => ({
        text: item.service_location,
        id: item.service_location,
        dataItemKey: "service_location",
      }));
      setServiceLocationSource({
        serviceLocationData: tempArray,
        isServiceLocationLoading: false,
        originalData: tempArray,
      });
    } else {
      setServiceLocationSource((prev) => ({
        ...prev,
        isServiceLocationLoading: false,
        serviceLocationData: [],
        originalData: [],
      }));
    }
  };

  const getPositionList = async (eventData) => {
    setPositionSourceData((prev) => ({
      ...prev,
      isPositionDataLoading: true,
      positionData: [],
      positionValue: {},
      originalData: [],
    }));

    const sourceObj = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_filter: null,
      in_event_type_mstr_key: eventData.eventTypeValue ? eventData.eventTypeValue.event_type_mstr_key : eventData,
      in_sort_number: 1,
      in_sort_order: "ASC",
    };

    const resName = await GetEventPositionFilterAPI(sourceObj, dispatch, history);

    if (resName.length > 0) {
      const tempArray = resName.map((item) => ({
        text: item.position_name,
        id: item.org_position_key.toString(),
        dataItemKey: "org_position_key",
      }));
      setPositionSourceData({
        positionData: tempArray,
        isPositionDataLoading: false,
        originalData: tempArray,
      });
    } else {
      setPositionSourceData((prev) => ({
        ...prev,
        isPositionDataLoading: false,
        positionData: [],
        originalData: [],
      }));
    }
  };

  const getCompPlanList = async (eventData) => {
    setCompPlanSource((prev) => ({
      ...prev,
      isCompPlanLoading: true,
      compPlanData: [],
      compPlanValue: {},
      originalData: [],
    }));

    const sourceObj = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_filter: null,
      in_event_type_mstr_key: eventData.eventTypeValue ? eventData.eventTypeValue.event_type_mstr_key : eventData,
      in_sort_number: 1,
      in_sort_order: "ASC",
    };

    const resName = await GetEventCompPlanFilterAPI(sourceObj, dispatch, history);

    if (resName.length > 0) {
      const tempArray = resName.map((item) => ({
        text: item.comp_plan_name,
        id: item.comp_plan_mstr_key.toString(),
        dataItemKey: "comp_plan_mstr_key",
      }));
      setCompPlanSource({
        compPlanData: tempArray,
        isCompPlanLoading: false,
        originalData: tempArray,
      });
    } else {
      setCompPlanSource((prev) => ({
        ...prev,
        isCompPlanLoading: false,
        compPlanData: [],
        originalData: [],
      }));
    }
  };

  const getApprovalStatusList = async () => {
    setApprovalStatusListData((prev) => ({
      ...prev,
      isApprovalStatusLoading: true,
      approvalStatusData: [],
      approvalStatusValue: {},
      originalData: [],
    }));

    const sourceObj = {
      in_filter: null,
      in_sort_number: 1,
      in_sort_order: "DESC",
      in_page_row_offset: 0,
      in_page_rows: 9999,
    };

    const resName = await getApprovalStatus(sourceObj, dispatch, history);

    if (resName.length > 0) {
      const tempArray = resName.map((item) => ({
        text: item.approval_status_name,
        id: item.approval_status_mstr_key.toString(),
        dataItemKey: "approval_status_mstr_key",
      }));
      setApprovalStatusListData({
        approvalStatusData: tempArray,
        isApprovalStatusLoading: false,
        originalData: tempArray,
      });
    } else {
      setApprovalStatusListData((prev) => ({
        ...prev,
        isApprovalStatusLoading: false,
        approvalStatusData: [],
        originalData: [],
      }));
    }
  };

  const getProcessStatusList = async () => {
    setProcessStatusListData((prev) => ({
      ...prev,
      isProcessStatusLoading: true,
      processStatusData: [],
      processStatusValue: {},
      originalData: [],
    }));

    const sourceObj = {
      in_filter: null,
      in_sort_number: 1,
      in_sort_order: "DESC",
    };

    const resName = await getProcessStatus(sourceObj, dispatch, history);

    if (resName.length > 0) {
      const tempArray = resName.map((item) => ({
        text: item.process_status_name,
        id: item.process_status_key.toString(),
        dataItemKey: "process_status_key",
      }));
      setProcessStatusListData({
        processStatusData: tempArray,
        isProcessStatusLoading: false,
        originalData: tempArray,
      });
    } else {
      setProcessStatusListData((prev) => ({
        ...prev,
        isProcessStatusLoading: false,
        processStatusData: [],
        originalData: [],
      }));
    }
  };

  const getEventAmountUnitTypes = async () => {
    setUnitTypeDataList((prev) => ({
      ...prev,
      isUnitTypeLoading: true,
      unitTypeData: [],
      unitTypeValue: {},
      originalData: [],
    }));

    let sourceObj = {
      in_filter: "AND (a.system_unit_name NOT LIKE 'fdm_%')",
      in_sort_number: null,
      in_sort_order: null,
      in_page_row_offset: 0,
      in_page_rows: 9999,
    };

    const resName = await GetUnitType(sourceObj, dispatch, history);

    if (resName.length > 0) {
      const tempArray = resName.map((item) => ({
        text: item.system_unit_name,
        id: item.system_unit_mstr_key.toString(),
        dataItemKey: "event_amount_unittype",
      }));
      setUnitTypeDataList({
        unitTypeData: tempArray,
        isUnitTypeLoading: false,
        originalData: tempArray,
      });
    } else {
      setUnitTypeDataList((prev) => ({
        ...prev,
        isUnitTypeLoading: false,
        unitTypeData: [],
        originalData: [],
      }));
    }
  };

  const getTotalAmountUnitTypes = async () => {
    setTotalAmtUnitTypeDataList((prev) => ({
      ...prev,
      isTotalAmtUnitTypeLoading: true,
      totalAmtTypeData: [],
      totalAmtUnitTypeValue: {},
      originalData: [],
    }));

    let sourceObj = {
      in_filter: "AND (a.system_unit_name NOT LIKE 'fdm_%')",
      in_sort_number: null,
      in_sort_order: null,
      in_page_row_offset: 0,
      in_page_rows: 9999,
    };

    const resName = await GetUnitType(sourceObj, dispatch, history);

    if (resName.length > 0) {
      const tempArray = resName.map((item) => ({
        text: item.system_unit_name,
        id: item.system_unit_mstr_key.toString(),
        dataItemKey: "total_amount_unittype",
      }));
      setTotalAmtUnitTypeDataList({
        totalAmtTypeData: tempArray,
        isTotalAmtUnitTypeLoading: false,
        originalData: tempArray,
      });
    } else {
      setTotalAmtUnitTypeDataList((prev) => ({
        ...prev,
        isTotalAmtUnitTypeLoading: false,
        totalAmtTypeData: [],
        originalData: [],
      }));
    }
  };
  return [
    {
      operatorList,
      eventTypeFields,
      setEventTypeFieldsData,
      eventDataSource,
      serviceLocationSource,
      compPlanSource,
      positionSourceData,
      dateFields,
      approvalStatusListData,
      eventTypeFieldList,
      isEventTypeChange,
      unitTypeDataList,
      totalAmtUnitTypeDataList,
      processStatusListData,
      setProcessStatusListData,
      setTotalAmtUnitTypeDataList,
      setUnitTypeDataList,
      setEventTypeFieldList,
      setApprovalStatusListData,
      setDateFields,
      getEventDataSourceDataListAsync,
      getServiceLocation,
      getPositionList,
      getCompPlanList,
      getApprovalStatusList,
      getEventTypeDropdown,
      getTotalAmountUnitTypes,
      getEventAmountUnitTypes,
      getProcessStatusList,
      fetchOperators,
      setEventDataSource,
      setServiceLocationSource,
      setCompPlanSource,
      setPositionSourceData,
      setIsEventTypeChange,
    },
  ];
};
